import { useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import classNames from "classnames";

import { golfLeagueRoutes } from "@/constants";
import type { SettingsRoute } from "@/constants";
import useSidebarStore from "@/zustand/useSidebarStore";
import useConfirmNavigation from "@/hooks/useConfirmNavigation";
import DownArrow from "@assets/tsxIcons/DownArrow";

/**
 * League Manager side panel — replaces the primary sidebar nav while active.
 * Mirrors the structure and styling of SettingsSidebarPanel so the two feel
 * like the same UX vocabulary.
 */
const LeagueSidebarPanel: React.FC = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const { setLeaguePanelOpen } = useSidebarStore();
  const { guardedNavigate } = useConfirmNavigation();

  const [expandedItem, setExpandedItem] = useState<number | null>(null);

  const currentRoutes: SettingsRoute[] = golfLeagueRoutes;

  const handleNavigate = (path: string, tabKey?: string) => {
    if (tabKey) {
      guardedNavigate(`${path}?tab=${tabKey}`);
    } else {
      guardedNavigate(path);
    }
  };

  const toggleExpanded = (index: number) => {
    setExpandedItem((prev) => (prev === index ? null : index));
  };

  const isActive = (path: string) => location.pathname === path;

  const isRouteActive = (item: SettingsRoute) => {
    if (isActive(item.path)) return true;
    if (item.subTabs) {
      return item.subTabs.some((st) => st.path && isActive(st.path));
    }
    return false;
  };

  const activeTabKey = searchParams.get("tab");

  return (
    <div className="flex flex-col h-full w-full">
      {/* Back to dashboard */}
      <div className="border-b border-[#3E4E66] flex gap-[8px] items-center pr-[8px] py-[8px] w-full">
        <div
          className="flex items-center justify-center size-[44px] cursor-pointer shrink-0"
          onClick={() => {
            setLeaguePanelOpen(false);
            handleNavigate("/dashboard");
          }}
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            className="rotate-180"
          >
            <path
              d="M9.5 5.5L16 12L9.5 18.5"
              stroke="#BCC9DE"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
        <span
          className="text-[16px] font-semibold text-[#BCC9DE] cursor-pointer whitespace-nowrap"
          onClick={() => {
            setLeaguePanelOpen(false);
            handleNavigate("/dashboard");
          }}
        >
          Back to dashboard
        </span>
      </div>

      {/* League Manager Header */}
      <div className="flex items-center justify-between px-[20px] py-[16px]">
        <span className="text-[22px] font-semibold text-[#FCFCFC]">
          League Manager
        </span>
        <div className="flex items-center justify-center size-[32px] border border-[#607496] rounded-[10px]">
          <span className="text-[12px] font-semibold text-[#57CC99]">LM</span>
        </div>
      </div>

      {/* Menu items */}
      <div className="flex-1 overflow-y-auto flex flex-col gap-[16px]">
        {currentRoutes.map((item, index) => {
          const active = isRouteActive(item);
          const isExpanded = expandedItem === index;
          const hasSubTabs = item.hasSubmenu && item.subTabs && item.subTabs.length > 0;
          return (
            <div key={index}>
              <div
                onClick={() => {
                  if (hasSubTabs) {
                    toggleExpanded(index);
                    if (!active) {
                      handleNavigate(item.path);
                    }
                  } else {
                    handleNavigate(item.path);
                  }
                }}
                className={classNames(
                  "flex items-center pl-[24px] pr-[16px] py-[11px] cursor-pointer transition-all duration-150 relative w-full",
                  {
                    "bg-[#293444]": active,
                    "hover:bg-[#293444]/60": !active,
                  }
                )}
              >
                {active && (
                  <div className="absolute left-0 top-0 bottom-0 w-[4px] bg-[#57CC99] rounded-r" />
                )}
                <span
                  className={classNames("text-[16px] font-normal flex-1 text-left", {
                    "text-[#FCFCFC]": active,
                    "text-[#8F9DB2]": !active,
                  })}
                >
                  {item.title}
                </span>
                {item.hasSubmenu && (
                  <DownArrow
                    className={classNames(
                      "fill-[#8F9DB2] size-[18px] shrink-0 ml-auto transition-transform duration-200",
                      { "rotate-180": isExpanded }
                    )}
                  />
                )}
              </div>
              {hasSubTabs && isExpanded && (
                <div className="flex flex-col">
                  {item.subTabs!.map((subTab) => {
                    const isSubActive = subTab.path
                      ? location.pathname === subTab.path
                      : active && activeTabKey === subTab.key;
                    return (
                      <div
                        key={subTab.key}
                        onClick={() => {
                          if (subTab.path) {
                            guardedNavigate(subTab.path);
                          } else {
                            handleNavigate(item.path, subTab.key);
                          }
                        }}
                        className={classNames(
                          "pl-[40px] pr-[16px] py-[9px] cursor-pointer transition-all duration-150 text-[14px] text-left",
                          {
                            "text-[#57CC99] font-medium": isSubActive,
                            "text-[#8F9DB2] hover:text-[#BCC9DE]": !isSubActive,
                          }
                        )}
                      >
                        {subTab.title}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default LeagueSidebarPanel;
